/**
 * Reusable list search utilities for token-based fuzzy matching and highlight ranges.
 * Use for autosuggest / filter lists where query tokens can appear in order with spaces between.
 * Supports typo tolerance: e.g. "orlanbo" matches "Orlando" within edit-distance threshold.
 */

/**
 * Normalize and split query into tokens (trim, collapse spaces, non-empty).
 * @param {string} query
 * @returns {string[]}
 */
export function getQueryTokens(query) {
  if (typeof query !== 'string' || !query.trim()) return []
  return query.trim().split(/\s+/).filter(Boolean)
}

/**
 * Levenshtein (edit) distance between two strings.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function levenshtein(a, b) {
  if (a.length === 0) return b.length
  if (b.length === 0) return a.length
  const matrix = []
  for (let i = 0; i <= b.length; i++) matrix[i] = [i]
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      const cost = a[j - 1].toLowerCase() === b[i - 1].toLowerCase() ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      )
    }
  }
  return matrix[b.length][a.length]
}

/** Max edit distance for fuzzy match: 1 for short tokens, 2 for longer. */
function maxFuzzyEdits(tokenLength) {
  return tokenLength <= 4 ? 1 : 2
}

/**
 * Get words in text with start/end indices (contiguous alphanumeric + #).
 * @param {string} text
 * @returns {{ word: string, start: number, end: number }[]}
 */
function getWordsWithPositions(text) {
  const result = []
  const re = /[#a-zA-Z0-9]+/g
  let m
  while ((m = re.exec(text)) !== null) {
    result.push({ word: m[0], start: m.index, end: m.index + m[0].length })
  }
  return result
}

/**
 * Find best match for token in text: exact substring first, else fuzzy (word with smallest edit distance).
 * @param {string} text
 * @param {string} token
 * @returns {{ start: number, end: number, exact: boolean } | null}
 */
function findTokenMatch(text, token) {
  if (!text || !token) return null
  const textLower = text.toLowerCase()
  const tokenLower = token.toLowerCase()

  // Tokens that are # + digits only: match the display id exactly as a whole word (no fuzzy, no substring inside #3750).
  if (/^#\d+$/.test(token)) {
    const words = getWordsWithPositions(text)
    for (const { word, start, end } of words) {
      if (word.toLowerCase() === tokenLower) {
        return { start, end, exact: true }
      }
    }
    return null
  }

  const exactIdx = textLower.indexOf(tokenLower)
  if (exactIdx !== -1) {
    return { start: exactIdx, end: exactIdx + token.length, exact: true }
  }

  const maxEdits = maxFuzzyEdits(token.length)
  const words = getWordsWithPositions(text)
  let best = null
  let bestDist = Infinity

  for (const { word, start, end } of words) {
    if (Math.abs(word.length - token.length) > maxEdits) continue
    const dist = levenshtein(token, word)
    if (dist <= maxEdits && dist < bestDist) {
      bestDist = dist
      best = { start, end, exact: false }
    }
  }
  return best
}

/**
 * Token-based match: every token must appear in text (case-insensitive), in any order.
 * Typo-tolerant: e.g. "orlanbo" matches "Orlando" (1 edit).
 * @param {string} query - Search query (e.g. "6 Orlando", "6 orlanbo")
 * @param {string} text - Text to search in
 * @returns {boolean}
 */
export function tokenMatch(query, text) {
  const tokens = getQueryTokens(query)
  if (tokens.length === 0) return true
  if (typeof text !== 'string' || !text) return false

  return tokens.every(token => findTokenMatch(text, token) !== null)
}

/**
 * Relevance score for sorting: higher = better match.
 * - Exact phrase (query as substring) ranks highest.
 * - Exact token matches rank higher than fuzzy (typo) matches.
 * - Tokens matching as "whole" rank higher; tokens closer together rank higher.
 * @param {string} query
 * @param {string} text
 * @returns {number} Score (0 or negative if no match; higher when more relevant)
 */
export function getMatchScore(query, text) {
  const tokens = getQueryTokens(query)
  if (tokens.length === 0 || typeof text !== 'string' || !text) return 0

  const textLower = text.toLowerCase()
  const phrase = query.trim().toLowerCase()
  if (phrase && textLower.includes(phrase)) return 1e6

  const positions = []
  for (const token of tokens) {
    const m = findTokenMatch(text, token)
    if (!m) return 0
    const before = m.start === 0 ? '' : text[m.start - 1]
    const after = m.end >= text.length ? '' : text[m.end]
    const isWordChar = c => /[a-zA-Z0-9]/.test(c)
    const isWhole = !isWordChar(before) && !isWordChar(after)
    positions.push({ start: m.start, end: m.end, isWhole, exact: m.exact })
  }

  if (positions.length === 0) return 0

  const firstStart = Math.min(...positions.map(p => p.start))
  const lastEnd = Math.max(...positions.map(p => p.end))
  const span = lastEnd - firstStart
  let wholeBonus = 0
  for (const p of positions) {
    if (p.exact && p.isWhole) wholeBonus += 100
    else if (p.exact) wholeBonus += 10
    else if (p.isWhole) wholeBonus += 5
    else wholeBonus += 1
  }

  return wholeBonus * 1000 - span
}

/**
 * Get non-overlapping [start, end) ranges (in original character indices) for each token match.
 * Uses exact or fuzzy match; for typos the matched word in text is highlighted (e.g. "Orlando" for "orlanbo").
 * @param {string} text - Full text (e.g. display string)
 * @param {string} query - Search query
 * @returns {{ start: number, end: number }[]}
 */
export function getHighlightRanges(text, query) {
  const tokens = getQueryTokens(query)
  if (tokens.length === 0 || typeof text !== 'string' || !text) return []

  const ranges = []
  for (const token of tokens) {
    const m = findTokenMatch(text, token)
    if (m) ranges.push({ start: m.start, end: m.end })
  }

  if (ranges.length === 0) return []

  ranges.sort((a, b) => a.start - b.start)
  const merged = [ranges[0]]
  for (let i = 1; i < ranges.length; i++) {
    const prev = merged[merged.length - 1]
    const curr = ranges[i]
    if (curr.start <= prev.end) {
      prev.end = Math.max(prev.end, curr.end)
    } else {
      merged.push(curr)
    }
  }
  return merged
}

/**
 * Split text into segments: [{ text, highlight }, ...] for rendering.
 * @param {string} text
 * @param {string} query
 * @returns {{ text: string, highlight: boolean }[]}
 */
export function getHighlightSegments(text, query) {
  const ranges = getHighlightRanges(text, query)
  if (ranges.length === 0) return [{ text, highlight: false }]

  const segments = []
  let lastEnd = 0

  for (const { start, end } of ranges) {
    if (start > lastEnd) {
      segments.push({ text: text.slice(lastEnd, start), highlight: false })
    }
    segments.push({ text: text.slice(start, end), highlight: true })
    lastEnd = end
  }
  if (lastEnd < text.length) {
    segments.push({ text: text.slice(lastEnd), highlight: false })
  }
  return segments
}
